'use client';
import React, { useState } from 'react';
import { inspectLighting, type RoomType, type InspectionResult, DEFAULT_FACTORS } from '@/lighting-app/data/lightingStandards';
import InspectProgress from './InspectProgress';
import InspectStepRoom from './InspectStepRoom';
import InspectStepDimensions from './InspectStepDimensions';
import InspectStepFixtures from './InspectStepFixtures';
import InspectResults from './InspectResults';

export interface InspectWizardState {
  step: number;
  roomType: RoomType | null;
  length: string;
  width: string;
  ceilingHeight: string;
  existingCount: string;
  lumensPerFixture: string;
  wattsPerFixture: string;
  fixtureTypeId: string;
  knowLumens: boolean;
  useAdvanced: boolean;
  uf: string;
  mf: string;
  result: InspectionResult | null;
}

const INITIAL_STATE: InspectWizardState = {
  step: 1,
  roomType: null,
  length: '',
  width: '',
  ceilingHeight: '',
  existingCount: '',
  lumensPerFixture: '',
  wattsPerFixture: '',
  fixtureTypeId: '',
  knowLumens: true,
  useAdvanced: false,
  uf: String(DEFAULT_FACTORS.uf),
  mf: String(DEFAULT_FACTORS.mf),
  result: null,
};

export default function InspectWizardClient() {
  const [state, setState] = useState<InspectWizardState>(INITIAL_STATE);

  const updateState = (updates: Partial<InspectWizardState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const handleInspect = () => {
    if (!state.roomType) return;
    const lumens = parseFloat(state.lumensPerFixture);
    if (!lumens) return;

    const result = inspectLighting({
      roomType: state.roomType,
      length: parseFloat(state.length),
      width: parseFloat(state.width),
      ceilingHeight: parseFloat(state.ceilingHeight) || 2.8,
      existingCount: parseInt(state.existingCount),
      lumensPerFixture: lumens,
      uf: parseFloat(state.uf),
      mf: parseFloat(state.mf),
      useAdvanced: state.useAdvanced,
    });

    updateState({ result, step: 4 });
  };

  const handleReset = () => setState(INITIAL_STATE);

  return (
    <div className="max-w-screen-lg mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-6">
        <span className="inline-flex items-center gap-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-semibold">
          🔍 فحص الإضاءة الموجودة
        </span>
      </div>

      <InspectProgress currentStep={state.step} />

      <div className="fade-in">
        {state.step === 1 && (
          <InspectStepRoom
            selected={state.roomType}
            onSelect={(room) => updateState({ roomType: room, step: 2 })}
          />
        )}
        {state.step === 2 && (
          <InspectStepDimensions
            state={state}
            onChange={updateState}
            onBack={() => updateState({ step: 1 })}
            onNext={() => updateState({ step: 3 })}
          />
        )}
        {state.step === 3 && (
          <InspectStepFixtures
            state={state}
            onChange={updateState}
            onBack={() => updateState({ step: 2 })}
            onInspect={handleInspect}
          />
        )}
        {state.step === 4 && state.result && (
          <InspectResults
            state={state}
            result={state.result}
            onBack={() => updateState({ step: 3 })}
            onReset={handleReset}
          />
        )}
      </div>
    </div>
  );
}